import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';

const PRESET_STEPS = {
  'SAP_CAP_BUILD_DEPLOY': [
    { name: 'MTA Build', command: 'mbt build -p=cf' },
    { name: 'CF Deploy (Blue-Green)', command: 'cf deploy mta_archives/hr-cap-project_1.0.0.mtar --strategy blue-green' }
  ],
  'SAP_CAP_BUILD_DEPLOY_SKIP': [
    { name: 'MTA Build', command: 'mbt build -p=cf' },
    { name: 'CF Deploy (Skip Testing)', command: 'cf deploy mta_archives/hr-cap-project_1.0.0.mtar --strategy blue-green --skip-testing-phase' }
  ],
  'SAP_CAP_BUILD': [
    { name: 'MTA Build', command: 'mbt build -p=cf' }
  ],
  'SAP_CAP_DEPLOY': [
    { name: 'CF Deploy (Blue-Green)', command: 'cf deploy mta_archives/hr-cap-project_1.0.0.mtar --strategy blue-green' }
  ],
  'SPRING_BOOT_BUILD_DEPLOY': [
    { name: 'Maven Build', command: 'mvn clean package -DskipTests' },
    { name: 'CF Push', command: 'cf push' }
  ],
  'SPRING_BOOT_BUILD': [
    { name: 'Maven Build', command: 'mvn clean package -DskipTests' }
  ],
  'REACT_BUILD': [
    { name: 'NPM Install', command: 'npm install' },
    { name: 'NPM Build', command: 'npm run build' }
  ],
  'CUSTOM': []
};

export default function Pipelines() {
  const [pipelines, setPipelines] = useState([]);
  const [runs, setRuns] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', projectId: '', type: 'BUILD_AND_DEPLOY', triggerType: 'MANUAL' });
  const [steps, setSteps] = useState([]);
  const [preset, setPreset] = useState('');
  const [editing, setEditing] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [liveRunId, setLiveRunId] = useState(null);
  const [triggeringId, setTriggeringId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [tab, setTab] = useState('pipelines');
  const [commits, setCommits] = useState([]);
  const [buildingCommitId, setBuildingCommitId] = useState(null);
  const [commitRuns, setCommitRuns] = useState({}); // commitId -> runData
  const [logVisible, setLogVisible] = useState({}); // commitId -> bool
  const [ngrok, setNgrok] = useState({ status: 'stopped', url: '' });
  const [ngrokLoading, setNgrokLoading] = useState(false);
  const logRef = useRef(null);

  const load = () => {
    api.get('/pipelines').then(r => setPipelines(r.data));
    api.get('/pipelines/runs').then(r => {
      setRuns(r.data);
      const running = r.data.find(run => run.status === 'RUNNING' || run.status === 'QUEUED');
      if (running && !liveRunId) {
        setLiveRunId(running.id);
        setSelectedRun(running);
      }
    });
    api.get('/projects').then(r => setProjects(r.data));
    api.get('/companies').then(r => setCompanies(r.data));
  };
  useEffect(() => {
    load();
    api.get('/webhooks/commits').then(r => setCommits(r.data)).catch(() => {});
    api.get('/ngrok/status').then(r => setNgrok(r.data)).catch(() => {});
  }, []);

  const startNgrok = async () => {
    setNgrokLoading(true);
    try {
      const { data } = await api.post('/ngrok/start');
      setNgrok(data);
    } catch (e) {
      alert('Failed to start ngrok: ' + (e.response?.data?.error || e.message));
    }
    setNgrokLoading(false);
  };

  const stopNgrok = async () => {
    setNgrokLoading(true);
    try {
      const { data } = await api.post('/ngrok/stop');
      setNgrok(data);
    } catch (e) {}
    setNgrokLoading(false);
  };

  // Auto-poll commits every 5s when on the Git Commits tab
  useEffect(() => {
    if (tab !== 'commits') return;
    const interval = setInterval(() => {
      api.get('/webhooks/commits').then(r => setCommits(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [tab]);

  // Poll ngrok status every 15s — detect if it dropped
  useEffect(() => {
    if (tab !== 'commits') return;
    const interval = setInterval(() => {
      api.get('/ngrok/status').then(r => {
        setNgrok(prev => {
          // If it was running but now stopped and user wanted it running → show alert state
          if (prev.status === 'running' && r.data.status === 'stopped' && r.data.wantRunning) {
            return { ...r.data, dropped: true };
          }
          return r.data;
        });
      }).catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [tab]);

  const refreshCommitRun = (commitId) => {
    api.get(`/webhooks/commits/${commitId}/run`).then(r => {
      if (r.data) {
        setCommitRuns(prev => ({ ...prev, [commitId]: r.data }));
        setLogVisible(prev => ({ ...prev, [commitId]: true }));
      }
    }).catch(() => {});
    // Also refresh commit status
    api.get('/webhooks/commits').then(r => {
      setCommits(r.data);
    }).catch(() => {});
  };

  const loadCommits = () => api.get('/webhooks/commits').then(r => {
    const data = r.data;
    setCommits(data);
    // Auto-trigger GIT_PUSH_AUTO pipelines for pending commits
    data.filter(c => c.status === 'PENDING' && c.projectId).forEach(c => {
      const autoPipeline = pipelines.find(p => p.projectId === c.projectId && p.triggerType === 'GIT_PUSH_AUTO');
      if (autoPipeline) startBuild(c);
    });
  }).catch(() => {});

  const cancelBuild = async (runId) => {
    try {
      await api.post(`/pipelines/runs/${runId}/cancel`);
    } catch (e) {
      alert('Could not cancel: ' + (e.response?.data?.error || e.message));
    }
  };

  const startBuild = async (commit) => {
    setBuildingCommitId(commit.id);
    // Prefer GIT_PUSH_MANUAL, then GIT_PUSH_AUTO, then any pipeline for this project
    const pipeline = pipelines.find(p => p.projectId === commit.projectId && p.triggerType === 'GIT_PUSH_MANUAL')
                  || pipelines.find(p => p.projectId === commit.projectId && p.triggerType === 'GIT_PUSH_AUTO')
                  || pipelines.find(p => p.projectId === commit.projectId);
    if (!pipeline) {
      alert('No pipeline found for this project. Please create one first.');
      setBuildingCommitId(null);
      return;
    }
    try {
      const { data } = await api.post(`/pipelines/${pipeline.id}/run`, {
        triggeredBy: 'git-push',
        branch: commit.branch,
        commitSha: commit.commitSha,
        commitMessage: commit.commitMessage,
      });
      const runId = data.runId;
      // Track run inline in Git Commits tab — no tab switch
      setCommitRuns(prev => ({ ...prev, [commit.id]: { id: runId, status: 'QUEUED', logs: 'Starting build...', currentStep: 0, totalSteps: 0 } }));
      setLogVisible(prev => ({ ...prev, [commit.id]: true }));
      // Poll run status every 2s
      const interval = setInterval(async () => {
        try {
          const { data: run } = await api.get(`/pipelines/runs/${runId}`);
          setCommitRuns(prev => ({ ...prev, [commit.id]: run }));
          if (run.status === 'SUCCESS' || run.status === 'FAILED' || run.status === 'CANCELLED') {
            clearInterval(interval);
            load();
            loadCommits();
          }
        } catch { clearInterval(interval); }
      }, 2000);
    } catch (e) { alert('Failed to start build: ' + (e.response?.data?.message || e.message)); }
    setBuildingCommitId(null);
  };

  const filteredProjects = filterCompany ? projects.filter(p => p.companyId === Number(filterCompany)) : projects;
  const filteredPipelines = pipelines.filter(p => {
    if (filterProject && p.projectId !== Number(filterProject)) return false;
    if (filterCompany && !filteredProjects.some(proj => proj.id === p.projectId)) return false;
    return true;
  });
  const filteredRuns = runs.filter(r => {
    const pipeline = pipelines.find(p => p.id === r.pipelineId);
    if (!pipeline) return true;
    if (filterProject && pipeline.projectId !== Number(filterProject)) return false;
    if (filterCompany && !filteredProjects.some(proj => proj.id === pipeline.projectId)) return false;
    return true;
  });

  // Auto-scroll logs to bottom
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [selectedRun?.logs]);

  // Poll for live run updates — refresh log panel + runs list every 2s
  useEffect(() => {
    if (!liveRunId) return;
    const interval = setInterval(() => {
      api.get(`/pipelines/runs/${liveRunId}`).then(r => {
        setSelectedRun(r.data);
        setRuns(prev => prev.map(run => run.id === r.data.id ? r.data : run));
        if (r.data.status === 'SUCCESS' || r.data.status === 'FAILED' || r.data.status === 'CANCELLED') {
          setLiveRunId(null);
          load();
        }
      }).catch(() => setLiveRunId(null));
    }, 2000);
    return () => clearInterval(interval);
  }, [liveRunId]);

  const applyPreset = (key) => {
    setPreset(key);
    setSteps(PRESET_STEPS[key] ? [...PRESET_STEPS[key].map(s => ({ ...s }))] : []);
  };

  const addStep = () => setSteps([...steps, { name: '', command: '' }]);
  const removeStep = (i) => setSteps(steps.filter((_, idx) => idx !== i));
  const updateStep = (i, field, val) => {
    const updated = [...steps];
    updated[i] = { ...updated[i], [field]: val };
    setSteps(updated);
  };

  const save = async () => {
    if (!form.name || !form.projectId) return alert('Name and Project are required');
    if (steps.length > 0 && steps.some(s => !s.command)) return alert('All steps must have a command');
    const payload = {
      ...form,
      projectId: Number(form.projectId),
      stepsJson: steps.length > 0 ? JSON.stringify(steps) : null
    };
    if (editing) {
      await api.put(`/pipelines/${editing}`, payload);
    } else {
      await api.post('/pipelines', payload);
    }
    setShowForm(false);
    setEditing(null);
    setForm({ name: '', description: '', projectId: '', type: 'BUILD_AND_DEPLOY', triggerType: 'MANUAL' });
    setSteps([]);
    setPreset('');
    load();
  };

  const editPipeline = (p) => {
    setForm({ name: p.name, description: p.description || '', projectId: String(p.projectId), type: p.type, triggerType: p.triggerType });
    setSteps(parseSteps(p.stepsJson));
    setEditing(p.id);
    setShowForm(true);
  };

  const trigger = async (id) => {
    setTriggeringId(id);
    try {
      const { data } = await api.post(`/pipelines/${id}/run`, { triggeredBy: 'admin' });
      setLiveRunId(data.runId);
      // Immediately show a placeholder so the log panel opens right away
      setSelectedRun({ id: data.runId, status: 'QUEUED', logs: 'Starting pipeline...', triggeredBy: 'admin', currentStep: 0, totalSteps: 0 });
      load();
    } catch (e) { console.error(e); }
    setTriggeringId(null);
  };

  const cancelRun = async (runId) => {
    setCancellingId(runId);
    try {
      await api.post(`/pipelines/runs/${runId}/cancel`, {});
      setLiveRunId(null);
      load();
    } catch (e) { console.error(e); }
    setCancellingId(null);
  };

  const del = async (id) => { if (window.confirm('Delete pipeline?')) { await api.delete(`/pipelines/${id}`); load(); } };

  const statusBadge = (s) => {
    const m = { SUCCESS: 'badge-success', FAILED: 'badge-danger', RUNNING: 'badge-warning', QUEUED: 'badge-info', CANCELLED: 'badge-gray' };
    return <span className={`badge ${m[s] || 'badge-gray'}`}>{s}</span>;
  };

  const projectName = (id) => projects.find(p => p.id === id)?.name || `#${id}`;

  const parseSteps = (json) => {
    try { return json ? JSON.parse(json) : []; } catch { return []; }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>Pipelines</h1>
          <p style={{ color: '#64748b', fontSize: 14 }}>Build and deploy your applications</p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>+ New Pipeline</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e2e8f0' }}>
        {[['pipelines', '🔄 Pipelines'], ['commits', '📦 Git Commits']].map(([key, label]) => (
          <button key={key} onClick={() => { setTab(key); if (key === 'commits') loadCommits(); }}
            style={{
              padding: '8px 20px', fontSize: 14, fontWeight: 500, border: 'none', cursor: 'pointer',
              background: 'none', borderBottom: tab === key ? '2px solid #1a56db' : '2px solid transparent',
              color: tab === key ? '#1a56db' : '#6b7280', marginBottom: -1,
            }}>
            {label}
            {key === 'commits' && commits.filter(c => c.status === 'PENDING').length > 0 && (
              <span style={{ marginLeft: 6, background: '#dc2626', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>
                {commits.filter(c => c.status === 'PENDING').length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Git Commits Tab */}
      {tab === 'commits' && (
        <div>
          {/* Webhook URL Panel */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: '10px 16px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#f0fdf4' }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: '#16a34a' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>GitHub Webhook URL</span>
              <span style={{ fontSize: 12, color: '#16a34a', marginLeft: 10, fontFamily: 'monospace' }}>
                https://devops.cloudleaders.co.in/api/webhooks/github
              </span>
            </div>
            <button onClick={() => navigator.clipboard.writeText('https://devops.cloudleaders.co.in/api/webhooks/github')}
              style={{ fontSize: 12, padding: '4px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>
              📋 Copy
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <select value={filterCompany} onChange={e => { setFilterCompany(e.target.value); setFilterProject(''); }}
                style={{ fontSize: 13, padding: '6px 10px', width: 'auto' }}>
                <option value="">All Companies</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
                style={{ fontSize: 13, padding: '6px 10px', width: 'auto' }}>
                <option value="">All Projects</option>
                {(filterCompany ? projects.filter(p => p.companyId === Number(filterCompany)) : projects).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {(filterCompany || filterProject) && (
                <button onClick={() => { setFilterCompany(''); setFilterProject(''); }}
                  style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer' }}>Clear</button>
              )}
            </div>
            <button onClick={loadCommits} title="Fetch latest commits" style={{ fontSize: 13, padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', color: '#374151' }}>↻ Refresh</button>
          </div>
          {(() => {
            const filteredCommits = commits.filter(c => {
              if (filterProject && c.projectId !== Number(filterProject)) return false;
              if (filterCompany) {
                const proj = projects.find(p => p.id === c.projectId);
                if (!proj || proj.companyId !== Number(filterCompany)) return false;
              }
              return true;
            });
            return filteredCommits.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
              <div>No commits received yet. Push to GitHub to see commits here.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredCommits.map(c => {
                const liveRun = commitRuns[c.id];
                const isBuilding = liveRun && (liveRun.status === 'QUEUED' || liveRun.status === 'RUNNING');
                const displayStatus = isBuilding ? 'BUILDING' : c.status;
                const dotColor = displayStatus === 'BUILDING' ? '#d97706' : displayStatus === 'BUILD_SUCCESS' ? '#16a34a' : displayStatus === 'BUILD_FAILED' ? '#dc2626' : displayStatus === 'DEPLOYED' ? '#1a56db' : displayStatus === 'PENDING' ? '#f59e0b' : '#94a3b8';
                const badgeBg = displayStatus === 'BUILDING' ? '#fef3c7' : displayStatus === 'BUILD_SUCCESS' ? '#dcfce7' : displayStatus === 'BUILD_FAILED' ? '#fee2e2' : displayStatus === 'DEPLOYED' ? '#dbeafe' : '#f1f5f9';
                const badgeColor = displayStatus === 'BUILDING' ? '#92400e' : displayStatus === 'BUILD_SUCCESS' ? '#166534' : displayStatus === 'BUILD_FAILED' ? '#991b1b' : displayStatus === 'DEPLOYED' ? '#1e40af' : '#6b7280';
                const badgeLabel = displayStatus === 'BUILDING' ? '⚙ BUILDING...' : displayStatus;
                return (
                <div key={c.id} style={{ background: '#fff', border: `1px solid ${isBuilding ? '#d97706' : liveRun ? '#1a56db' : '#e2e8f0'}`, borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: dotColor }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <code style={{ fontSize: 12, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{c.commitSha}</code>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>on</span>
                      <span style={{ fontSize: 12, color: '#1a56db', fontWeight: 600 }}>{c.branch}</span>
                      <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, fontWeight: 600, background: badgeBg, color: badgeColor }}>{badgeLabel}</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#0f172a', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.commitMessage}
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>
                      by <strong>{c.author}</strong> · {c.filesChanged} file(s) changed · {c.githubRepo} · {new Date(c.receivedAt).toLocaleString()}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                      Project: <strong style={{ color: '#0f172a' }}>{c.projectId ? `${projectName(c.projectId)} (#${c.projectId})` : '⚠ Not matched'}</strong>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0, alignItems: 'flex-end' }}>
                    {c.commitUrl && (
                      <a href={c.commitUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', textDecoration: 'none' }}>
                        View on GitHub
                      </a>
                    )}
                    <button onClick={() => logVisible[c.id] ? setLogVisible(prev => ({ ...prev, [c.id]: false })) : refreshCommitRun(c.id)}
                      title={logVisible[c.id] ? 'Close log' : 'View build log'}
                      style={{ fontSize: 13, padding: '5px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: logVisible[c.id] ? '#0f172a' : '#f8fafc', cursor: 'pointer', color: logVisible[c.id] ? '#e2e8f0' : '#374151' }}>
                      {logVisible[c.id] ? '▲ Close Log' : '📋 View Log'}
                    </button>
                    {c.status === 'PENDING' && (() => {
                      const autoPipeline   = pipelines.find(p => p.projectId === c.projectId && p.triggerType === 'GIT_PUSH_AUTO');
                      const manualPipeline = pipelines.find(p => p.projectId === c.projectId && p.triggerType === 'GIT_PUSH_MANUAL');
                      const anyPipeline    = pipelines.find(p => p.projectId === c.projectId);
                      const chosenPipeline = manualPipeline || autoPipeline || anyPipeline;
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                          <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right' }}>
                            Pipeline: <strong style={{ color: '#374151' }}>{chosenPipeline?.name || 'None'}</strong>
                          </div>
                          {autoPipeline && !manualPipeline ? (
                            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>
                              ⚡ Auto Build triggered
                            </span>
                          ) : isBuilding ? (
                            <button onClick={() => cancelBuild(liveRun.id)}
                              style={{ fontSize: 13, padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600,
                                background: '#fee2e2', color: '#991b1b' }}>
                              ⏹ Stop Build
                            </button>
                          ) : (
                            <button onClick={() => startBuild(c)} disabled={buildingCommitId === c.id}
                              style={{ fontSize: 13, padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600,
                                background: buildingCommitId === c.id ? '#e2e8f0' : '#1a56db',
                                color: buildingCommitId === c.id ? '#94a3b8' : '#fff' }}>
                              {buildingCommitId === c.id ? 'Starting...' : '▶ Start Build'}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                {/* Inline log panel */}
                {logVisible[c.id] && commitRuns[c.id] && (
                  <div style={{ borderTop: '1px solid #e2e8f0', background: '#0f172a', padding: '10px 16px' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                      {statusBadge(commitRuns[c.id].status)}
                      {commitRuns[c.id].totalSteps > 0 && (
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>Step {commitRuns[c.id].currentStep}/{commitRuns[c.id].totalSteps}</span>
                      )}
                    </div>
                    {commitRuns[c.id].totalSteps > 0 && (
                      <div style={{ height: 4, background: '#1e293b', borderRadius: 2, marginBottom: 8 }}>
                        <div style={{ height: '100%', borderRadius: 2, transition: 'width 0.3s',
                          width: `${(commitRuns[c.id].currentStep / commitRuns[c.id].totalSteps) * 100}%`,
                          background: commitRuns[c.id].status === 'FAILED' ? '#dc2626' : commitRuns[c.id].status === 'SUCCESS' ? '#059669' : '#d97706'
                        }} />
                      </div>
                    )}
                    <pre style={{ color: '#e2e8f0', fontSize: 11, margin: 0, maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {commitRuns[c.id].logs || 'Waiting for logs...'}
                    </pre>
                  </div>
                )}
                </div>
              );
              })}
            </div>
          );
          })()}
        </div>
      )}

      {tab === 'pipelines' && (
      <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: '#6b7280' }}>Filter:</label>
        <select value={filterCompany} onChange={e => { setFilterCompany(e.target.value); setFilterProject(''); }}
          style={{ width: 'auto', fontSize: 13, padding: '6px 10px' }}>
          <option value="">All Companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
          style={{ width: 'auto', fontSize: 13, padding: '6px 10px' }}>
          <option value="">All Projects</option>
          {filteredProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {(filterCompany || filterProject) && (
          <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }}
            onClick={() => { setFilterCompany(''); setFilterProject(''); }}>Clear</button>
        )}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 16, fontWeight: 600 }}>{editing ? 'Edit Pipeline' : 'New Pipeline'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Pipeline Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. HR App Full Deploy" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Project</label>
              <select value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })}>
                <option value="">Select project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option value="BUILD">Build Only</option>
                <option value="DEPLOY">Deploy Only</option>
                <option value="BUILD_AND_DEPLOY">Build & Deploy</option>
                <option value="ROLLBACK">Rollback</option>
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Trigger</label>
              <select value={form.triggerType} onChange={e => setForm({ ...form, triggerType: e.target.value })}>
                <option value="MANUAL">Manual</option>
                <option value="GIT_PUSH_AUTO">Git Push — Auto Build (no confirmation)</option>
                <option value="GIT_PUSH_MANUAL">Git Push — Manual Build (confirm first)</option>
                <option value="SCHEDULED">Scheduled</option>
                <option value="WEBHOOK">Webhook</option>
              </select>
            </div>
          </div>

          {/* Pipeline Steps */}
          <div style={{ marginTop: 8, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <label style={{ fontSize: 14, fontWeight: 600 }}>Pipeline Steps</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={preset} onChange={e => applyPreset(e.target.value)}
                  style={{ width: 'auto', fontSize: 12, padding: '4px 8px' }}>
                  <option value="">Load preset...</option>
                  <optgroup label="SAP CAP">
                    <option value="SAP_CAP_BUILD_DEPLOY">Build + Deploy (Blue-Green)</option>
                    <option value="SAP_CAP_BUILD_DEPLOY_SKIP">Build + Deploy (Skip Testing)</option>
                    <option value="SAP_CAP_BUILD">Build Only</option>
                    <option value="SAP_CAP_DEPLOY">Deploy Only</option>
                  </optgroup>
                  <optgroup label="Spring Boot">
                    <option value="SPRING_BOOT_BUILD_DEPLOY">Build + Deploy</option>
                    <option value="SPRING_BOOT_BUILD">Build Only</option>
                  </optgroup>
                  <optgroup label="React">
                    <option value="REACT_BUILD">Install + Build</option>
                  </optgroup>
                  <option value="CUSTOM">Custom (empty)</option>
                </select>
                <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={addStep}>+ Add Step</button>
              </div>
            </div>

            {steps.length === 0 ? (
              <div style={{ padding: 16, background: '#f9fafb', borderRadius: 8, border: '1px dashed #d1d5db', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                No custom steps — will auto-detect commands based on project stack. Select a preset or add steps manually.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {steps.map((step, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 8, alignItems: 'center', padding: 12,
                    background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0'
                  }}>
                    <span style={{ color: '#6b7280', fontWeight: 600, fontSize: 13, minWidth: 24 }}>{i + 1}</span>
                    <input placeholder="Step name" value={step.name}
                      onChange={e => updateStep(i, 'name', e.target.value)}
                      style={{ width: 200 }} />
                    <input placeholder="Command (e.g. mbt build -p=cf)" value={step.command}
                      onChange={e => updateStep(i, 'command', e.target.value)}
                      style={{ flex: 1, fontFamily: 'monospace', fontSize: 13 }} />
                    <button className="btn-danger" style={{ padding: '4px 8px', fontSize: 11 }}
                      onClick={() => removeStep(i)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-primary" onClick={save}>{editing ? 'Update Pipeline' : 'Create Pipeline'}</button>
            <button className="btn-ghost" onClick={() => { setShowForm(false); setEditing(null); setSteps([]); setPreset(''); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, fontWeight: 600 }}>Pipelines</h3>
        {filteredPipelines.length === 0 ? <p style={{ color: '#9ca3af' }}>No pipelines configured</p> : (
          <table>
            <thead><tr><th>Name</th><th>Project</th><th>Type</th><th>Steps</th><th>Trigger</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredPipelines.map(p => {
                const pSteps = parseSteps(p.stepsJson);
                return (
                  <tr key={p.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.name}</div>
                      {pSteps.length > 0 && (
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                          {pSteps.map(s => s.name || s.command).join(' → ')}
                        </div>
                      )}
                    </td>
                    <td>{projectName(p.projectId)} <span style={{ fontSize: 11, color: '#94a3b8' }}>(#{p.projectId})</span></td>
                    <td><span className="badge badge-info">{p.type}</span></td>
                    <td><span className="badge badge-gray">{pSteps.length || 'auto'}</span></td>
                    <td>{p.triggerType}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-success" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => trigger(p.id)} disabled={!!liveRunId || triggeringId === p.id}>
                          {triggeringId === p.id ? '⏳ Starting...' : liveRunId ? '⏳ Running...' : '▶ Run'}
                        </button>
                        <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => editPipeline(p)}>Edit</button>
                        <button className="btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => del(p.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedRun ? '1fr 1fr' : '1fr', gap: 20 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ fontWeight: 600 }}>Recent Runs</h3>
              <button onClick={load} title="Refresh" style={{
                background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
                color: '#6b7280', padding: '2px 4px', lineHeight: 1
              }}>↻</button>
            </div>
            {filteredRuns.length > 0 && (
              <button className="btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={async () => {
                if (window.confirm('Clear all run logs?')) {
                  try {
                    await api.post('/pipelines/runs/clear', {});
                    setSelectedRun(null);
                    setLiveRunId(null);
                    setRuns([]);
                  } catch (e) { console.error(e); }
                }
              }}>Clear All Logs</button>
            )}
          </div>
          {filteredRuns.length === 0 ? <p style={{ color: '#9ca3af', fontSize: 14 }}>No runs yet. Click ▶ Run on a pipeline above.</p> : (
            <table>
              <thead><tr><th>Run</th><th>Status</th><th>Progress</th><th>Triggered By</th><th>Time</th><th></th></tr></thead>
              <tbody>
                {filteredRuns.map(r => (
                  <tr key={r.id} onClick={() => { setSelectedRun(r); if (r.status === 'RUNNING') setLiveRunId(r.id); }}
                    style={{ cursor: 'pointer' }}>
                    <td style={{ fontWeight: 500 }}>#{r.id}</td>
                    <td>{statusBadge(r.status)}</td>
                    <td style={{ fontSize: 13 }}>{r.totalSteps ? `${r.currentStep}/${r.totalSteps}` : '-'}</td>
                    <td>{r.triggeredBy || '-'}</td>
                    <td style={{ fontSize: 13, color: '#6b7280' }}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {(r.status === 'RUNNING' || r.status === 'QUEUED') && (
                          <button className="btn-danger" style={{ padding: '2px 8px', fontSize: 11 }}
                            disabled={cancellingId === r.id}
                            onClick={e => { e.stopPropagation(); cancelRun(r.id); }}>
                            {cancellingId === r.id ? '⏳' : 'Cancel'}
                          </button>
                        )}
                        <button className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }} onClick={async (e) => {
                          e.stopPropagation();
                          await api.delete(`/pipelines/runs/${r.id}`);
                          if (selectedRun?.id === r.id) setSelectedRun(null);
                          if (liveRunId === r.id) setLiveRunId(null);
                          load();
                        }}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedRun && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 600 }}>
                Run #{selectedRun.id} Logs
                {liveRunId === selectedRun.id && <span style={{ marginLeft: 8, fontSize: 12, color: '#d97706' }}>● LIVE</span>}
              </h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {(selectedRun.status === 'RUNNING' || selectedRun.status === 'QUEUED') && (
                  <button className="btn-danger" style={{ padding: '4px 10px', fontSize: 12 }}
                    disabled={cancellingId === selectedRun.id}
                    onClick={() => cancelRun(selectedRun.id)}>
                    {cancellingId === selectedRun.id ? 'Cancelling...' : 'Cancel Run'}
                  </button>
                )}
                <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => { setSelectedRun(null); setLiveRunId(null); }}>Close</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {statusBadge(selectedRun.status)}
              <span style={{ fontSize: 13, color: '#6b7280' }}>by {selectedRun.triggeredBy || 'system'}</span>
              {selectedRun.totalSteps > 0 && (
                <span style={{ fontSize: 13, color: '#6b7280' }}>• Step {selectedRun.currentStep}/{selectedRun.totalSteps}</span>
              )}
            </div>
            {selectedRun.totalSteps > 0 && (
              <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, marginBottom: 12, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 3, transition: 'width 0.3s',
                  width: `${(selectedRun.currentStep / selectedRun.totalSteps) * 100}%`,
                  background: selectedRun.status === 'FAILED' ? '#dc2626' : selectedRun.status === 'SUCCESS' ? '#059669' : '#d97706'
                }} />
              </div>
            )}
            <pre ref={logRef} style={{
              background: '#0f172a', color: '#e2e8f0', padding: 16, borderRadius: 8,
              fontSize: 12, lineHeight: 1.6, maxHeight: 500, overflow: 'auto', whiteSpace: 'pre-wrap'
            }}>{selectedRun.logs || 'Waiting for logs...'}</pre>
          </div>
        )}
      </div>
      </div>
      )}
    </div>
  );
}
